import React, { useState } from 'react'

import Highlight from '../../Highlight'
import type { ActivityTypeComponentProps } from '../Activity'
import ActivityFooter from '../ActivityFooter'
import ActivityTitle from '../ActivityTitle'
import QuoteActivitySubtitle from '../QuoteActivitySubtitle'
import HtmlContent from '../../HtmlContent'
import { formatName, getCrews, translateEventType } from '@paintscout/util/builder'
import type { EventActivityDetails } from '@paintscout/api'
import { useClientOptions } from '../../../providers/ClientOptionsProvider'
import ActivityContent from '../ActivityContent'
import Collapse from '../../Collapse'
import IconButton from '../../IconButton'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import classnames from 'classnames'
import Typography from '../../Typography'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'

const useStyles = makeStyles<Theme, ActivityTypeComponentProps>((theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: 'fit-content',
    '& > div': {
      width: '100%'
    }
  },
  email: {
    maxHeight: 150,
    overflowY: 'auto',
    display: 'flex'
  },
  emailTo: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.paintscout.palette.brand.Neutral.P60
  },
  emailSubject: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    cursor: 'pointer'
  },
  emailContent: {
    color: theme.palette.text.secondary,
    textAlign: 'left',
    marginTop: theme.spacing(0.5)
  },
  collapseText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer'
  }
}))

export default function EventActivity(props: ActivityTypeComponentProps) {
  const classes = useStyles(props)
  const { activity, showDocTypeDetails } = props
  const { options } = useClientOptions()
  const activityType = activity.details?.activityType
  const eventType = (activity.details as EventActivityDetails)?.production
    ? 'Job'
    : translateEventType({ eventType: (activity.details as EventActivityDetails)?.eventType, options })
  const action = activityType === 'event-created' ? 'scheduled' : (activityType || '').replace('event-', '')

  const crewOptions = getCrews({ options })
  const crew = crewOptions.find((crew) => crew._id === (activity.details as any).crew)

  let actionPostfix = ''
  switch (activityType) {
    case 'event-created':
      actionPostfix = ' for '
      break
    case 'event-assigned':
      actionPostfix = ' to '
      break
    case 'event-cancelled':
      actionPostfix = ' by '
      break
  }

  const ownerToDisplay = ['event-cancelled', 'event-progress-updated'].includes(activityType)
    ? activity.details.owner
    : crew
      ? crew.projectManager
      : activity.details.quoteOwner

  const [collapsed, setCollapsed] = useState(true)
  const handleCollapseChange = (e, isOpen: boolean) => {
    e.preventDefault()
    e.stopPropagation()
    setCollapsed(isOpen)
  }
  const note = (activity.details as EventActivityDetails)?.note || ''
  const showContent = note.length > 0

  const TitleComponent = () => {
    if (activityType === 'event-progress-updated') {
      const progress = (activity.details as EventActivityDetails)?.progress
      if (progress === 100) {
        return (
          <>
            <Highlight>{eventType}</Highlight> was <Highlight>completed</Highlight>
          </>
        )
      }
      return (
        <>
          <Highlight>{eventType} progress</Highlight> was <Highlight>updated</Highlight>
        </>
      )
    } else {
      return (
        <>
          <Highlight>{eventType}</Highlight> was <Highlight>{action}</Highlight>
          {actionPostfix}
          <Highlight>{formatName(ownerToDisplay)}</Highlight>
        </>
      )
    }
  }

  return (
    <>
      <ActivityTitle>
        <TitleComponent />
      </ActivityTitle>
      {showContent && (
        <ActivityContent className={classes.content}>
          <Collapse show={collapsed}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.collapseText}
              style={{ height: 16, width: 'fit-content' }}
              onClick={(e) => handleCollapseChange(e, false)}
            >
              View description
              <IconButton size="small" style={{ marginLeft: 8 }}>
                <KeyboardArrowDownIcon />
              </IconButton>
            </Typography>
          </Collapse>
          <Collapse show={!collapsed}>
            <>
              {note && (
                <div className={classes.email}>
                  <HtmlContent content={note} classes={{ root: classes.emailContent }} />
                  <Typography
                    variant="body1"
                    component="h5"
                    className={classnames(classes.emailSubject)}
                    onClick={(e) => handleCollapseChange(e, true)}
                  >
                    <IconButton size="small">
                      <KeyboardArrowUpIcon />
                    </IconButton>
                  </Typography>
                </div>
              )}
            </>
          </Collapse>
        </ActivityContent>
      )}
      <QuoteActivitySubtitle activity={activity} showDocTypeDetails={showDocTypeDetails} />
      <ActivityFooter timestamp={activity.details.timestamp} />
    </>
  )
}
